import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Alert, Spin, Button, Descriptions, Text } from '@bancobarigui/barihaus';
import { Empty, Collapse, message, Form, Input } from 'antd';
import { QuestionRenderer } from '@/components/DetailInspection/InspectionChecklist/QuestionRenderer';
import { UploadOutlined, SearchOutlined, DeleteOutlined, SaveOutlined, EditOutlined } from '@ant-design/icons';
import { ImageEditorModal, type ImageEditorResult } from '@/components/ImageEditorModal';
import { evaluateDisplayRule } from '@/utils/displayRuleEvaluator';

import {
  VistoriaData,
  IdentificacaoData,
  WizardMode,
} from '@/features/laudo/types/laudo-wizard.types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { responseService } from '@/services/checklists/response.service';
import { checklistService } from '@/services/checklists/checklist.service';
import { GroupedResponses } from '@/features/laudo/types/checklist-response.types';
import { groupChecklistResponses } from '@/features/laudo/utils/groupChecklistResponses';
import { AnswerDisplay } from '@/features/laudo/components/AnswerDisplay';
import { streetViewService } from '@/services/streetview.service';
import { refreshChecklistPhotos, blobToBase64 } from '@/features/laudo/pdf/utils/imageUtils';
import { docmanService } from '@/services/docman.service';
import { ReviewFieldWrapper } from '../../ReviewFieldWrapper';
import { useReviewMode } from '../../index';
import { PhotoOrderManager } from './PhotoOrderManager';
import { Checklist } from '@/services/checklists';
import type { ChecklistAnswer, SubmitChecklistResponseRequest } from '@/services/types/checklist.types';
import { QuestionAnswerType } from '@/services/types/checklist.types';

const { Panel } = Collapse;

const answerTypeMap: Record<string, QuestionAnswerType> = {
  'ShortAnswer': QuestionAnswerType.ShortAnswer,
  'LongAnswer': QuestionAnswerType.LongAnswer,
  'RadioButton': QuestionAnswerType.RadioButton,
  'Checkbox': QuestionAnswerType.Checkbox,
  'YesNo': QuestionAnswerType.YesNo,
  'Date': QuestionAnswerType.Date,
  'NumericInteger': QuestionAnswerType.NumericInteger,
  'NumericDecimal': QuestionAnswerType.NumericDecimal,
  'Currency': QuestionAnswerType.Currency,
  'Table': QuestionAnswerType.Table,
  'Dropdown': QuestionAnswerType.Dropdown,
  'Scale': QuestionAnswerType.Scale,
  'Photo': QuestionAnswerType.Photo,
  'Signature': QuestionAnswerType.Signature,
  'File': QuestionAnswerType.File,
  'Rating': QuestionAnswerType.Rating,
  'Location': QuestionAnswerType.Location,
};

const findQuestionById = (checklist: Checklist, questionId: string) => {
  for (const group of checklist.groups) {
    for (const section of group.sections) {
      const question = section.questions.find(q => q.id === questionId);
      if (question) return question;
    }
  }
  return null;
};

interface VistoriaStepProps {
  data: VistoriaData;
  onUpdate: (data: Partial<VistoriaData>) => void;
  inspection: InspectionRequest;
  identificacaoData?: IdentificacaoData; // Dados de endereço atualizados do wizard
  mode?: WizardMode;
}

export function VistoriaStep({
  data,
  onUpdate,
  inspection,
  identificacaoData,
  mode = 'create',
}: VistoriaStepProps) {
  const isReviewMode = mode === 'review';
  const isCorrectionMode = mode === 'correction';
  const { reviewContext } = useReviewMode();

  // Verifica se é devolução externa (status 21) - sem correções pendentes específicas
  const isExternalReturn = isCorrectionMode && (!reviewContext?.pendingCorrections || reviewContext.pendingCorrections.length === 0);

  // Verifica se o campo de foto de fachada tem correção pendente
  const hasFotoFachadaCorrection = isExternalReturn || reviewContext?.pendingCorrections?.some(
    c => c.fieldPath === 'vistoria.fotoFachada'
  );

  // Botões de foto de fachada só aparecem se:
  // - NÃO está em modo de revisão E
  // - (NÃO está em modo de correção OU tem correção pendente para esse campo)
  const showFotoFachadaActions = !isReviewMode && (!isCorrectionMode || hasFotoFachadaCorrection);
  const [loading, setLoading] = useState(false);
  const [groupedResponses, setGroupedResponses] = useState<GroupedResponses[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [checklistName, setChecklistName] = useState<string>('');
  const [vistoriaDate, setVistoriaDate] = useState<string>('');
  const [loadingStreetView, setLoadingStreetView] = useState(false);
  const [streetViewOptions, setStreetViewOptions] = useState<
    Array<{ heading: number; base64: string; label: string }>
  >([]);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [fullChecklist, setFullChecklist] = useState<Checklist | null>(null);
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [isEditingChecklist, setIsEditingChecklist] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasFetchedRef = useRef(false);
  const hasFetchedPhotoRef = useRef(false);
  // Estado atual da vistoria para os callbacks assíncronos — evita que o download
  // da foto antiga sobrescreva uma foto que o usuário trocou/removeu nesse meio-tempo.
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  const [rawResponses, setRawResponses] = useState<any[]>([]);
  const [isEditingFotoFachada, setIsEditingFotoFachada] = useState(false);
  const [editingChecklistPhoto, setEditingChecklistPhoto] = useState<{ photo: any; questionId: string } | null>(null);
  const [savingChecklistPhoto, setSavingChecklistPhoto] = useState(false);

  // Avalia a resposta da própria pergunta para mostrar campo de justificativa (padrão mobile)
  const shouldShowJustification = useCallback((question: any): boolean => {
    if (!question.hasDisplayRule || !question.displayRule) return false;
    const answer = formValues?.[question.id];
    if (answer === undefined || answer === null || answer === '') return false;
    return evaluateDisplayRule(question.displayRule, answer);
  }, [formValues]);

  // Computa os valores para popular o form de edição do checklist
  const savedFormValues = useMemo(() => {
    if (!fullChecklist || rawResponses.length === 0) return {};
    const values: Record<string, any> = {};
    rawResponses.forEach((response: any) => {
      const question = findQuestionById(fullChecklist, response.questionId);
      if (!question) return;
      const answerType = answerTypeMap[question.answerType];
      const answer = response.answer;
      if (!answer) return;
      switch (answerType) {
        case QuestionAnswerType.ShortAnswer:
        case QuestionAnswerType.LongAnswer:
          values[response.questionId] = answer.textValue;
          break;
        case QuestionAnswerType.RadioButton:
        case QuestionAnswerType.YesNo:
        case QuestionAnswerType.Dropdown:
          values[response.questionId] = answer.selectedOption;
          break;
        case QuestionAnswerType.Checkbox:
          values[response.questionId] = answer.selectedOptions || [];
          break;
        case QuestionAnswerType.Date:
          values[response.questionId] = answer.dateValue;
          break;
        case QuestionAnswerType.NumericInteger:
        case QuestionAnswerType.NumericDecimal:
        case QuestionAnswerType.Currency:
        case QuestionAnswerType.Scale:
        case QuestionAnswerType.Rating:
          values[response.questionId] = answer.numericValue;
          break;
        case QuestionAnswerType.Table:
          values[response.questionId] = answer.tableData || [];
          break;
        case QuestionAnswerType.Photo: {
          const photos = answer.photos || [];
          values[response.questionId] = {
            fileList: photos.map((photo: any, idx: number) => ({
              uid: photo.fileHash || `photo-${idx}`,
              name: photo.fileName || `Foto ${idx + 1}`,
              status: 'done' as const,
              url: photo.fileUrl,
              thumbUrl: photo.fileUrl,
              documentId: photo.fileHash,
            })),
          };
          break;
        }
        case QuestionAnswerType.File: {
          const files = answer.files || [];
          values[response.questionId] = {
            fileList: files.map((file: any, idx: number) => ({
              uid: file.fileHash || `file-${idx}`,
              name: file.fileName || `Arquivo ${idx + 1}`,
              status: 'done' as const,
              url: file.fileUrl,
              type: file.fileType,
              documentId: file.fileHash,
            })),
          };
          break;
        }
      }

      // Carregar justificativa/notes para campos condicionais (perguntas vinculadas)
      if (answer.notes) {
        values[`${response.questionId}_justification`] = answer.notes;
      }
    });
    return values;
  }, [fullChecklist, rawResponses]);

  // Carregar foto de fachada como base64 via backend (proxy) ao editar um laudo.
  // Usa downloadFile pra evitar fetch direto no S3 que falhava no pipeline do PDF.
  useEffect(() => {
    if (data.fotoFachadaDocumentId && !data.fotoFachada && !hasFetchedPhotoRef.current) {
      hasFetchedPhotoRef.current = true;
      const documentIdBaixado = data.fotoFachadaDocumentId;

      docmanService
        .downloadFile(documentIdBaixado)
        .then(blobToBase64)
        .then((base64) => {
          // Usuário pode ter anexado outra foto (ou removido) enquanto o
          // download rodava — aplicar aqui reverteria a troca para a foto antiga.
          const atual = dataRef.current;
          if (atual.fotoFachadaDocumentId !== documentIdBaixado || atual.fotoFachada) {
            return;
          }
          onUpdate({ fotoFachada: base64 });
        })
        .catch((err) => {
          console.error('[fotoFachada/VistoriaStep] Falha ao carregar via backend:', err);
          message.error('Erro ao carregar foto de fachada do servidor');
          hasFetchedPhotoRef.current = false;
        });
    }
  }, [data.fotoFachadaDocumentId, data.fotoFachada, onUpdate]);

  useEffect(() => {
    // Evitar chamadas duplicadas
    if (hasFetchedRef.current) {
      return;
    }

    const fetchChecklistResponses = async () => {
      // Obter IDs diretamente do inspection
      const firstItem = inspection.items?.[0];
      const inspectionItemId = inspection.id;
      const inspectionTypeId = firstItem?.inspectionTypeId;

      if (!inspectionItemId || !inspectionTypeId) {
        setError('Checklist ou item de inspeção não encontrado');
        return;
      }

      setLoading(true);
      setError(null);
      hasFetchedRef.current = true;

      try {
        const checklistResponse = await checklistService.getChecklistByInspectionType(
          inspectionTypeId,
          1,
          1
        );

        if (!checklistResponse?.items?.[0]) {
          // Laudo Automático - não há checklist, isso é esperado
          // Não mostrar erro, apenas deixar a seção de checklist vazia
          setLoading(false);
          return;
        }

        const checklist = checklistResponse.items[0];
        const checklistId = checklist.id;

        setFullChecklist(checklist as Checklist)

        const responses = await responseService.getResponsesByInspectionId(
          checklistId,
          inspectionItemId
        );

        const responsesArray = Array.isArray(responses) ? responses : [];

        const checklistData = checklist as any;
        if (checklistData?.name) {
          setChecklistName(checklistData.name);
        }

        if (responsesArray.length > 0) {
          const firstResponse = responsesArray[0] as any;
          if (firstResponse?.respondedAt) {
            setVistoriaDate(firstResponse.respondedAt);
          }
        }

        const grouped = groupChecklistResponses(responsesArray as any, checklist);

        setGroupedResponses(grouped);

        // Guardar respostas brutas para popular o form quando clicar em "Editar"
        setRawResponses(responsesArray);

        // Fazer refresh das URLs das fotos aqui no step de Vistória
        // para que quando chegar no step de Transmissão, tudo já esteja pronto
        // Fazer refresh em background sem bloquear a UI
        refreshChecklistPhotos(responsesArray)
          .then((processedResponses) => {
            // Salvar respostas processadas no estado do wizard
            onUpdate({ processedResponses });
          })
          .catch(() => {
            // Mesmo com erro, salvar as respostas originais para tentar no TransmissaoStep
            onUpdate({ processedResponses: responsesArray });
          });
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar respostas do checklist');
        hasFetchedRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    fetchChecklistResponses();
  }, [inspection.id]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      message.error('Por favor, selecione apenas arquivos de imagem');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      message.error('A imagem deve ter no máximo 10MB');
      return;
    }

    setUploadingPhoto(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result as string;
      // Zera documentId pra forçar upload da nova imagem no transmit.
      onUpdate({ fotoFachada: base64String, fotoFachadaDocumentId: undefined });
      message.success('Foto anexada com sucesso');
      setUploadingPhoto(false);
    };
    reader.onerror = () => {
      message.error('Erro ao carregar a foto');
      setUploadingPhoto(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    onUpdate({ fotoFachada: '', fotoFachadaCaption: '', fotoFachadaDocumentId: undefined });
    message.success('Foto removida');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFotoFachadaEditSave = (result: ImageEditorResult) => {
    if (result.wasRotated && result.rotatedBase64) {
      // Zera o documentId pra forçar re-upload da imagem rotacionada no transmit.
      onUpdate({
        fotoFachada: result.rotatedBase64,
        fotoFachadaCaption: result.caption,
        fotoFachadaDocumentId: undefined,
      });
    } else {
      onUpdate({ fotoFachadaCaption: result.caption });
    }
    setIsEditingFotoFachada(false);
    message.success('Foto da fachada atualizada!');
  };

  const handleEditChecklistPhoto = (photo: any, questionId: string) => {
    setEditingChecklistPhoto({ photo, questionId });
  };

  const handleSaveChecklistPhotoEdit = async (result: ImageEditorResult) => {
    if (!editingChecklistPhoto || !fullChecklist) return;

    const { photo, questionId } = editingChecklistPhoto;

    try {
      setSavingChecklistPhoto(true);

      let newFileHash = photo.fileHash;
      let newFileUrl = photo.fileUrl;

      // If rotated, upload new image via docman
      if (result.wasRotated && result.rotatedFile) {
        const uploadResult = await docmanService.uploadFile(
          inspection.id,
          result.rotatedFile,
          'FOTOS_IMOVEL',
        );
        newFileHash = uploadResult.documentId;
        newFileUrl = await docmanService.getImageUrl(uploadResult.documentId);
      }

      // Update the photo in rawResponses
      const updatedRawResponses = rawResponses.map((resp: any) => {
        if (resp.questionId !== questionId) return resp;

        const updatedPhotos = (resp.answer?.photos || []).map((p: any) => {
          if (p.fileHash !== photo.fileHash) return p;
          return {
            ...p,
            fileHash: newFileHash,
            fileUrl: newFileUrl,
            caption: result.caption,
          };
        });

        return {
          ...resp,
          answer: { ...resp.answer, photos: updatedPhotos },
        };
      });

      // Build answers from updatedRawResponses and re-submit
      const answers: ChecklistAnswer[] = [];
      for (const resp of updatedRawResponses) {
        const question = findQuestionById(fullChecklist, resp.questionId);
        if (!question) continue;

        const answerType = answerTypeMap[question.answerType];
        if (answerType === undefined) continue;

        const answer: ChecklistAnswer = {
          questionId: resp.questionId,
          questionAnswerType: answerType,
          isNotApplicable: resp.answer?.isNotApplicable || false,
        };

        switch (answerType) {
          case QuestionAnswerType.ShortAnswer:
          case QuestionAnswerType.LongAnswer:
            answer.textValue = resp.answer?.textValue;
            break;
          case QuestionAnswerType.RadioButton:
          case QuestionAnswerType.YesNo:
          case QuestionAnswerType.Dropdown:
            answer.selectedOption = resp.answer?.selectedOption;
            break;
          case QuestionAnswerType.Checkbox:
            answer.selectedOptions = resp.answer?.selectedOptions || [];
            break;
          case QuestionAnswerType.Date:
            answer.dateValue = resp.answer?.dateValue;
            break;
          case QuestionAnswerType.NumericInteger:
          case QuestionAnswerType.NumericDecimal:
          case QuestionAnswerType.Currency:
          case QuestionAnswerType.Scale:
          case QuestionAnswerType.Rating:
            answer.numericValue = resp.answer?.numericValue;
            break;
          case QuestionAnswerType.Table:
            answer.tableData = resp.answer?.tableData || [];
            break;
          case QuestionAnswerType.Photo:
            answer.photos = resp.answer?.photos || [];
            break;
          case QuestionAnswerType.File:
            answer.files = resp.answer?.files || [];
            break;
          default:
            answer.textValue = resp.answer?.textValue;
        }

        // Preserva justificativa (notes) das perguntas vinculadas no re-submit
        if (resp.answer?.notes) {
          answer.notes = resp.answer.notes;
        }

        answers.push(answer);
      }

      const payload: SubmitChecklistResponseRequest = {
        checklistId: fullChecklist.id,
        inspectionId: inspection.id,
        inspectionItemId: inspection.items?.[0]?.id,
        answers,
      };

      await responseService.submitResponse(fullChecklist.id, payload);

      // Re-fetch updated responses
      const freshResponses = await responseService.getResponsesByInspectionId(
        fullChecklist.id,
        inspection.id
      );
      const freshArray = Array.isArray(freshResponses) ? freshResponses : [];

      setRawResponses(freshArray);
      setGroupedResponses(groupChecklistResponses(freshArray as any, fullChecklist));

      // Refresh photo URLs for PDF
      refreshChecklistPhotos(freshArray)
        .then((processedResponses) => onUpdate({ processedResponses }))
        .catch(() => onUpdate({ processedResponses: freshArray }));

      setEditingChecklistPhoto(null);
      message.success('Foto do checklist atualizada!');
    } catch (err: any) {
      message.error(err?.message || 'Erro ao atualizar foto do checklist');
    } finally {
      setSavingChecklistPhoto(false);
    }
  };

  const handleBuscarAutomaticamente = async () => {
    const firstItem = inspection.items?.[0];

    if (!firstItem) {
      message.error('Item de inspeção não encontrado');
      return;
    }

    // Usar dados do wizard (identificacaoData) se disponíveis, senão usar do inspection
    const street = identificacaoData?.logradouro || firstItem.street;
    const number = identificacaoData?.numero || firstItem.number;
    const district = identificacaoData?.bairro || firstItem.district;
    const city = identificacaoData?.cidade || firstItem.city;
    const state = identificacaoData?.estado || firstItem.state;
    const cep = identificacaoData?.cep || firstItem.cep;

    const address = `${street}, ${number}, ${district}, ${city}/${state}, ${cep}`;

    if (!street || !city) {
      message.error(
        'Endereço do imóvel incompleto. Por favor, preencha e salve os dados na aba Identificação.'
      );
      return;
    }

    setLoadingStreetView(true);

    try {
      message.loading({
        content: 'Buscando imagens em diferentes ângulos...',
        key: 'streetview',
        duration: 0,
      });

      const availability = await streetViewService.checkAddressAvailability(address);

      if (!availability.available) {
        message.warning({
          content:
            availability.message || 'Endereço não encontrado. Tente anexar uma foto manualmente.',
          key: 'streetview',
          duration: 5,
        });
        setLoadingStreetView(false);
        return;
      }

      const images = await streetViewService.fetchMultipleStreetViewAngles(address);

      setStreetViewOptions(images);

      message.success({
        content: 'Imagens carregadas! Escolha o melhor ângulo abaixo.',
        key: 'streetview',
        duration: 3,
      });
    } catch (error: any) {
      message.error({
        content: error.message || 'Erro ao buscar imagem do endereço. Tente anexar manualmente.',
        key: 'streetview',
        duration: 5,
      });
    } finally {
      setLoadingStreetView(false);
    }
  };

  const handleSelectStreetViewImage = (base64Image: string) => {
    onUpdate({ fotoFachada: base64Image, fotoFachadaDocumentId: undefined });
    setStreetViewOptions([]);
    message.success('Foto selecionada com sucesso!');
  };

  const handleCancelStreetViewSelection = () => {
    setStreetViewOptions([]);
  };

  const handlePhotoOrderChange = (order: string[]) => {
    onUpdate({ photoOrder: order });
  };

  const handleStartEditChecklist = () => {
    if (!fullChecklist) return;
    setIsEditingChecklist(true);
    // Pre-popula campos de justificativa (perguntas vinculadas) no form,
    // pois o Form.Item de justificativa não recebe initialValue.
    const justificationValues: Record<string, any> = {};
    Object.keys(savedFormValues).forEach((key) => {
      if (key.endsWith('_justification')) {
        justificationValues[key] = savedFormValues[key];
      }
    });
    if (Object.keys(justificationValues).length > 0) {
      setTimeout(() => {
        form.setFieldsValue(justificationValues);
      }, 0);
    }
  };

  const uploadFile = async (file: any, questionTitle: string): Promise<{ documentId: string; fileUrl: string }> => {
    // Foto pré-existente: já tem documentId no Docman, não reupload.
    // fileUrl é gravado no formato canônico "docman/<id>" — não a URL pre-signed,
    // que expira e quebraria o reload do checklist depois.
    if (file.documentId) {
      return {
        documentId: file.documentId,
        fileUrl: `docman/${file.documentId}`,
      };
    }

    const originalFile = file.originFileObj || file;
    if (!originalFile || !(originalFile instanceof File)) {
      throw new Error(`Arquivo inválido para a pergunta "${questionTitle}"`);
    }

    const documentType = originalFile.type.startsWith('image/') ? 'FOTOS_IMOVEL' : 'OUTROS';
    const result = await docmanService.uploadFile(
      inspection.id,
      originalFile,
      documentType,
    );

    return {
      documentId: result.documentId,
      fileUrl: `docman/${result.documentId}`,
    };
  };

  const handleSaveChecklist = async () => {
    if (!fullChecklist) return;

    try {
      setSavingChecklist(true);
      const values = form.getFieldsValue();
      const answers: ChecklistAnswer[] = [];

      for (const group of fullChecklist.groups) {
        for (const section of group.sections) {
          for (const question of section.questions) {
            const value = values[question.id];

            if (value === undefined || value === null || value === '') {
              continue;
            }

            const answerType = answerTypeMap[question.answerType];
            if (answerType === undefined) continue;

            const answer: ChecklistAnswer = {
              questionId: question.id,
              questionAnswerType: answerType,
              isNotApplicable: false,
            };

            switch (answerType) {
              case QuestionAnswerType.ShortAnswer:
              case QuestionAnswerType.LongAnswer:
                answer.textValue = value;
                break;
              case QuestionAnswerType.RadioButton:
              case QuestionAnswerType.YesNo:
              case QuestionAnswerType.Dropdown:
                answer.selectedOption = value;
                break;
              case QuestionAnswerType.Checkbox:
                answer.selectedOptions = Array.isArray(value) ? value : [value];
                break;
              case QuestionAnswerType.Date:
                answer.dateValue = value;
                break;
              case QuestionAnswerType.NumericInteger:
              case QuestionAnswerType.NumericDecimal:
              case QuestionAnswerType.Currency:
              case QuestionAnswerType.Scale:
              case QuestionAnswerType.Rating:
                answer.numericValue = typeof value === 'number' ? value : parseFloat(value);
                break;
              case QuestionAnswerType.Table:
                answer.tableData = Array.isArray(value) ? value : [];
                break;
              case QuestionAnswerType.Photo:
                if (value && value.fileList && Array.isArray(value.fileList)) {
                  const photoUploads = value.fileList.map(async (file: any) => {
                    const uploadResult = await uploadFile(file, question.title);
                    return {
                      fileName: file.name || file.fileName,
                      fileUrl: uploadResult.fileUrl,
                      bucketPath: '',
                      bucketName: '',
                      caption: '',
                      uploadedAt: new Date().toISOString(),
                      fileSizeBytes: file.size || 0,
                      imageWidth: 0,
                      imageHeight: 0,
                      fileHash: uploadResult.documentId,
                    };
                  });
                  answer.photos = await Promise.all(photoUploads);
                } else if (Array.isArray(value)) {
                  answer.photos = value;
                } else {
                  answer.photos = [];
                }
                break;
              case QuestionAnswerType.File:
                if (value && value.fileList && Array.isArray(value.fileList)) {
                  const fileUploads = value.fileList.map(async (file: any) => {
                    const uploadResult = await uploadFile(file, question.title);
                    return {
                      fileName: file.name || file.fileName,
                      fileUrl: uploadResult.fileUrl,
                      fileType: file.type || '',
                      bucketPath: '',
                      bucketName: '',
                      description: '',
                      uploadedAt: new Date().toISOString(),
                      fileSizeBytes: file.size || 0,
                      fileHash: uploadResult.documentId,
                    };
                  });
                  answer.files = await Promise.all(fileUploads);
                } else if (Array.isArray(value)) {
                  answer.files = value;
                } else {
                  answer.files = [];
                }
                break;
              default:
                answer.textValue = value;
            }

            // Preserva justificativa (notes) das perguntas vinculadas
            const justificationKey = `${question.id}_justification`;
            const justificationValue = values[justificationKey];
            if (question.hasDisplayRule && question.displayRule) {
              const shouldInclude = evaluateDisplayRule(question.displayRule, value);
              if (shouldInclude && justificationValue) {
                answer.notes = justificationValue;
              }
            } else if (justificationValue) {
              answer.notes = justificationValue;
            }

            answers.push(answer);
          }
        }
      }

      const payload: SubmitChecklistResponseRequest = {
        checklistId: fullChecklist.id,
        inspectionId: inspection.id,
        inspectionItemId: inspection.items?.[0]?.id,
        answers,
      };

      await responseService.submitResponse(fullChecklist.id, payload);

      // Re-buscar respostas atualizadas do backend
      const updatedResponses = await responseService.getResponsesByInspectionId(
        fullChecklist.id,
        inspection.id
      );
      const updatedArray = Array.isArray(updatedResponses) ? updatedResponses : [];

      // Atualizar estados locais
      setRawResponses(updatedArray);
      setGroupedResponses(groupChecklistResponses(updatedArray as any, fullChecklist));

      // Atualizar data da vistoria se disponível
      if (updatedArray.length > 0) {
        const firstResponse = updatedArray[0] as any;
        if (firstResponse?.respondedAt) {
          setVistoriaDate(firstResponse.respondedAt);
        }
      }

      // Refresh das URLs das fotos para o PDF (em background)
      refreshChecklistPhotos(updatedArray)
        .then((processedResponses) => {
          onUpdate({ processedResponses });
        })
        .catch(() => {
          onUpdate({ processedResponses: updatedArray });
        });

      // Voltar para modo de visualização
      setIsEditingChecklist(false);
      message.success('Checklist salvo com sucesso!');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Erro ao salvar checklist');
    } finally {
      setSavingChecklist(false);
    }
  };

  return (
    <div className="vistoria-step">
      <h4 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Dados da Vistoria</h4>

      <Descriptions
        column={{ xs: 1 }}
        bordered
        size="small"
        items={[
          {
            key: 'dataVistoria',
            label: <Text strong>Data da Vistoria</Text>,
            children: vistoriaDate
              ? new Date(vistoriaDate).toLocaleDateString('pt-BR')
              : data.dataVistoria
                ? new Date(data.dataVistoria).toLocaleDateString('pt-BR')
                : 'N/A',
          },
          {
            key: 'vistoriador',
            label: <Text strong>Vistoriador</Text>,
            children: data.vistoriador,
          },
          {
            key: 'checklist',
            label: <Text strong>Checklist</Text>,
            children: checklistName || data.checklistId || 'Checklist não associado',
          },
        ]}
      />

      <h4 style={{ fontSize: 16, fontWeight: 600, marginTop: 24, marginBottom: 16 }}>
        Checklist Técnico
      </h4>

      {loading ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            backgroundColor: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
          }}
        >
          <Spin size="large" />
          <p style={{ color: '#8c8c8c', marginTop: 16 }}>Carregando respostas do checklist...</p>
        </div>
      ) : error ? (
        <Alert message="Erro ao carregar checklist" description={error} type="error" showIcon />
      ) : groupedResponses.length === 0 && !checklistName ? (
        <div
          style={{
            padding: 24,
            backgroundColor: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: 8,
          }}
        >
          <Alert
            message="Laudo Automático"
            description="Este tipo de inspeção não possui checklist vinculado. A avaliação será feita diretamente pela Prestadora."
            type="info"
            showIcon
          />
        </div>
      ) : groupedResponses.length === 0 ? (
        <div
          style={{
            padding: 24,
            backgroundColor: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
          }}
        >
          <Empty description="Nenhuma resposta encontrada no checklist" />
        </div>
      ) : isEditingChecklist && fullChecklist ? (
        <Form form={form} layout="vertical">
          <Collapse
            defaultActiveKey={fullChecklist.groups.map(g => g.id)}
            expandIconPosition="start"
            style={{ background: '#fff' }}
          >
            {fullChecklist.groups.map((group) => (
              <Panel
                key={group.id}
                header={<span style={{ fontWeight: 600, fontSize: 14 }}>{group.name}</span>}
              >
                {group.sections.map((section) => (
                  <div key={section.id} style={{ marginBottom: 24 }}>
                    <h5
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        marginBottom: 12,
                        color: '#595959',
                        borderBottom: '1px solid #e8e8e8',
                        paddingBottom: 8,
                      }}
                    >
                      {section.name}
                    </h5>
                    <div style={{ paddingLeft: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {section.questions.map((question) => {
                        const showJustification = shouldShowJustification(question);
                        return (
                          <div
                            key={question.id}
                            style={{
                              padding: 16,
                              background: '#fafafa',
                              borderRadius: 6,
                              border: '1px solid #f0f0f0',
                            }}
                          >
                            <QuestionRenderer
                              question={question}
                              inspectionItemId={inspection.items?.[0]?.id}
                              initialValue={savedFormValues[question.id]}
                            />
                            {showJustification && (
                              <div style={{ marginTop: 12 }}>
                                <label style={{ display: 'block', fontWeight: 500, marginBottom: 4, fontSize: 13 }}>
                                  Justificativa *
                                </label>
                                <Form.Item
                                  name={`${question.id}_justification`}
                                  rules={[{ required: true, message: 'Justificativa obrigatória' }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input.TextArea
                                    rows={3}
                                    maxLength={500}
                                    placeholder="Informe a justificativa"
                                    showCount
                                  />
                                </Form.Item>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </Panel>
            ))}
          </Collapse>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16 }}>
            <Button
              onClick={() => setIsEditingChecklist(false)}
            >
              Cancelar
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingChecklist}
              onClick={handleSaveChecklist}
            >
              Salvar Checklist
            </Button>
          </div>
        </Form>
      ) : (
        <ReviewFieldWrapper
          fieldPath="vistoria.checklist"
          fieldLabel="Checklist da Vistoria"
          stepName="Vistoria"
          layout="stacked"
          allowEditAllInCorrection
        >
          {fullChecklist && !isReviewMode && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button
                type="primary"
                size="small"
                onClick={handleStartEditChecklist}
              >
                Editar Checklist
              </Button>
            </div>
          )}
          <Collapse
            defaultActiveKey={groupedResponses[0]?.groupId ? [groupedResponses[0].groupId] : []}
            accordion={false}
          >
            {groupedResponses.map((group) => (
              <Panel
                header={<span style={{ fontWeight: 600, fontSize: 14 }}>{group.groupName}</span>}
                key={group.groupId}
              >
                {group.sections.map((section) => (
                  <div key={section.sectionId} style={{ marginBottom: 24 }}>
                    <h5
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        marginBottom: 12,
                        color: '#595959',
                        borderBottom: '1px solid #e8e8e8',
                        paddingBottom: 8,
                      }}
                    >
                      {section.sectionName}
                    </h5>
                    <div style={{ paddingLeft: 12 }}>
                      {section.responses.map((response) => (
                        <AnswerDisplay key={response.id} response={response} onEditPhoto={handleEditChecklistPhoto} />
                      ))}
                    </div>
                  </div>
                ))}
              </Panel>
            ))}
          </Collapse>
        </ReviewFieldWrapper>
      )}

      <div style={{ marginTop: 24 }}>
        <ReviewFieldWrapper
          fieldPath="vistoria.fotoFachada"
          fieldLabel="Foto da Fachada"
          stepName="Vistoria"
        >
          <div>
            <h4 style={{ fontSize: 16, fontWeight: 600, margin: 0, marginBottom: 16 }}>
              Foto da Fachada
            </h4>

            {/* Botões de ação - escondidos no modo de revisão e no modo de correção (exceto se o campo foi marcado) */}
            {showFotoFachadaActions && (
              <div style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => fileInputRef.current?.click()}
                  loading={uploadingPhoto}
                  disabled={uploadingPhoto}
                >
                  Anexar Foto
                </Button>
                <Button
                  icon={<SearchOutlined />}
                  onClick={handleBuscarAutomaticamente}
                  loading={loadingStreetView}
                  disabled={loadingStreetView}
                >
                  Buscar Automaticamente
                </Button>
                {data.fotoFachada && (
                  <>
                    <Button icon={<EditOutlined />} onClick={() => setIsEditingFotoFachada(true)}>
                      Editar Foto
                    </Button>
                    <Button danger icon={<DeleteOutlined />} onClick={handleRemovePhoto}>
                      Remover Foto
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Preview da foto */}
            <div
              style={{
                padding: 24,
                backgroundColor: '#fafafa',
                border: '1px solid #d9d9d9',
                borderRadius: 8,
                textAlign: 'center',
                minHeight: 200,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {data.fotoFachada ? (
                <div style={{ maxWidth: '100%' }}>
                  <img
                    src={data.fotoFachada}
                    alt="Fachada do imóvel"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 400,
                      objectFit: 'contain',
                      borderRadius: 4,
                    }}
                  />
                  {data.fotoFachadaCaption && (
                    <p style={{ marginTop: 8, color: '#595959', fontSize: 13, fontStyle: 'italic' }}>
                      {data.fotoFachadaCaption}
                    </p>
                  )}
                </div>
              ) : (
                <div>
                  <UploadOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
                  <p style={{ color: '#8c8c8c', margin: 0 }}>Nenhuma foto da fachada disponível</p>
                  {showFotoFachadaActions && (
                    <p style={{ color: '#bfbfbf', fontSize: 12, marginTop: 8 }}>
                      Clique em "Anexar Foto" ou "Buscar Automaticamente"
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </ReviewFieldWrapper>
      </div>

      {/* Seletor de ângulos do Street View */}
      {streetViewOptions.length > 0 && (
        <div
          style={{
            marginTop: 24,
            padding: 20,
            backgroundColor: '#f0f5ff',
            border: '2px solid #1890ff',
            borderRadius: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 16,
            }}
          >
            <div>
              <h5 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#1890ff' }}>
                Selecione a melhor foto da fachada
              </h5>
              <p style={{ fontSize: 13, color: '#595959', marginTop: 4, marginBottom: 0 }}>
                Clique na imagem que melhor representa a fachada do imóvel
              </p>
            </div>
            <Button size="small" onClick={handleCancelStreetViewSelection}>
              Cancelar
            </Button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 16,
            }}
          >
            {streetViewOptions.map((option) => (
              <div
                key={option.heading}
                style={{
                  border: '2px solid #d9d9d9',
                  borderRadius: 8,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                  backgroundColor: '#fff',
                  position: 'relative',
                }}
                onClick={() => handleSelectStreetViewImage(option.base64)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#1890ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d9d9d9';
                }}
              >
                {/* Badge de "Clique para selecionar" */}
                <div
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: '#fff',
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    zIndex: 1,
                  }}
                >
                  Clique para usar
                </div>

                <img
                  src={option.base64}
                  alt={`Visão ${option.label}`}
                  style={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                  }}
                />
                <div
                  style={{
                    padding: 12,
                    backgroundColor: '#fafafa',
                    textAlign: 'center',
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#262626',
                  }}
                >
                  {option.label} ({option.heading}°)
                </div>
              </div>
            ))}
          </div>

          {/* Dica adicional */}
          <div
            style={{
              marginTop: 16,
              padding: 12,
              backgroundColor: '#fff',
              borderRadius: 6,
              border: '1px solid #d9d9d9',
              fontSize: 12,
              color: '#8c8c8c',
            }}
          >
            <strong>Dica:</strong> Escolha a foto que mostra melhor a frente do imóvel. Você pode
            cancelar e anexar uma foto manualmente se preferir.
          </div>
        </div>
      )}

      {/* Gerenciador de ordem das fotos - apenas se houver fotos no checklist */}
      {!isReviewMode && groupedResponses.length > 0 && (
        <PhotoOrderManager
          groupedResponses={groupedResponses}
          currentOrder={data.photoOrder}
          onOrderChange={handlePhotoOrderChange}
        />
      )}

      <ImageEditorModal
        open={isEditingFotoFachada}
        onCancel={() => setIsEditingFotoFachada(false)}
        onSave={handleFotoFachadaEditSave}
        imageSrc={data.fotoFachada}
        caption={data.fotoFachadaCaption || ''}
        title="Editar Foto da Fachada"
      />

      <ImageEditorModal
        open={!!editingChecklistPhoto}
        onCancel={() => setEditingChecklistPhoto(null)}
        onSave={handleSaveChecklistPhotoEdit}
        documentId={editingChecklistPhoto?.photo?.fileHash}
        caption={editingChecklistPhoto?.photo?.caption || ''}
        saving={savingChecklistPhoto}
        title="Editar Foto do Checklist"
      />
    </div>
  );
}
